import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { adminApi as defaultAdminApi } from './admin-api';
import { generateTemporaryPassword, safeGenerateTemporaryPassword } from '../lib/temporary-password';

const MIN_PASSWORD_LENGTH = 8;

/**
 * "Restablecer contraseña" inside a member's detail (spec: "Si el alumno
 * olvida su clave, un administrador genera/asigna otra contraseña temporal
 * desde el detalle del socio. Esto vuelve a activar mustChangePassword.").
 *
 * Reuses the same "generate up front, editable, regenerable, fail closed
 * without Web Crypto" behavior as `MemberForm`'s password field (Task 8),
 * plus a two-step confirmation naming the exact account (username + email)
 * before sending anything — this action immediately revokes every session
 * the member has open, so the admin has to see who they're about to reset
 * before it happens, not just trust which detail page they're on.
 *
 * Sends only `{ temporaryPassword }`: the target id already lives in the URL
 * `POST /members/:id/reset-password` builds (`adminApi.resetMemberPassword`),
 * never in the body.
 */
export function ResetPasswordForm({
  api = defaultAdminApi,
  memberId,
  memberUsername,
  memberEmail,
  memberDocumentNumber,
  generatePassword = generateTemporaryPassword,
  onReset,
  onCancel,
}) {
  const { handleApiError } = useAuth();
  const [passwordState, setPasswordState] = useState(() => safeGenerateTemporaryPassword(generatePassword));
  const [useDocumentAsPassword, setUseDocumentAsPassword] = useState(Boolean(memberDocumentNumber));
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function regenerate() {
    setPasswordState(safeGenerateTemporaryPassword(generatePassword));
    setConfirming(false);
  }

  function askConfirm(event) {
    event.preventDefault();
    setError('');
    if (!useDocumentAsPassword && (!passwordState.password || passwordState.password.length < MIN_PASSWORD_LENGTH)) {
      setError('La contraseña temporal debe tener al menos 8 caracteres.');
      return;
    }
    setConfirming(true);
  }

  function cancelConfirm() {
    setConfirming(false);
  }

  async function confirmReset() {
    setSubmitting(true);
    setError('');
    try {
      if (useDocumentAsPassword) await api.resetMemberPassword(memberId, undefined, true);
      else await api.resetMemberPassword(memberId, passwordState.password);
      onReset(useDocumentAsPassword ? { password: null, passwordIsDocument: true } : passwordState.password);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      setError(errorMessageFor(caught));
      setConfirming(false);
      setSubmitting(false);
    }
  }

  return (
    <form className="gym-form gym-card gym-admin-reset-form" onSubmit={askConfirm} noValidate aria-label="Restablecer contraseña">
      {error && <p role="alert" className="gym-form__error">{error}</p>}
      {passwordState.error && <p role="alert" className="gym-form__error">{passwordState.error}</p>}

      {memberDocumentNumber && <div className="gym-field">
        <label className="gym-admin-checkbox">
          <input
            type="checkbox"
            name="useDocumentAsPassword"
            checked={useDocumentAsPassword}
            disabled={submitting || confirming}
            onChange={(event) => setUseDocumentAsPassword(event.target.checked)}
          />
          Usar el DNI como contraseña
        </label>
      </div>}

      {!useDocumentAsPassword && <div className="gym-field">
        <label htmlFor="reset-password" className="gym-field__label">Contraseña temporal</label>
        <div className="gym-admin-password-row">
          <input
            id="reset-password"
            name="temporaryPassword"
            type="text"
            autoComplete="off"
            spellCheck="false"
            required
            value={passwordState.password}
            disabled={submitting || confirming}
            onChange={(event) => setPasswordState({ password: event.target.value, error: '' })}
            className="gym-input gym-admin-password-row__input"
          />
          <button
            type="button"
            className="gym-button gym-button--secondary"
            disabled={submitting || confirming}
            onClick={regenerate}
          >
            Regenerar
          </button>
        </div>
      </div>}

      {confirming ? (
        <div className="gym-plans-confirm">
          <span className="gym-plans-confirm__text">
            {'¿Restablecer la contraseña de '}
            <strong>{memberUsername}</strong>
            {` (${memberEmail})? Se van a cerrar sus sesiones actuales.`}
          </span>
          <button type="button" className="gym-button gym-button--danger" disabled={submitting} onClick={confirmReset}>
            {submitting ? 'Restableciendo…' : 'Sí, restablecer'}
          </button>
          <button type="button" className="gym-button gym-button--secondary" disabled={submitting} onClick={cancelConfirm}>
            Cancelar
          </button>
        </div>
      ) : (
        <div className="gym-admin-form-actions">
          <button type="submit" className="gym-button gym-button--primary">Restablecer contraseña</button>
          {onCancel && (
            <button type="button" className="gym-button gym-button--secondary" onClick={onCancel}>Cancelar</button>
          )}
        </div>
      )}
    </form>
  );
}
